<template>
  <div style="clear: both">
    <template v-if="readonly">
      <v-text-field
        v-bind:model-value="user && user.displayName ? user.displayName : null"
        :label="label"
        placeholder=" "
        persistent-placeholder
        variant="underlined"
        :readonly="readonly"
        :append-icon="user && user.displayName ? 'mdi-information-outline' : undefined"
        @click:append="showHideVCard"
        dense
        density="comfortable"
      ></v-text-field>
    </template>
    <template v-if="!readonly">
      <v-autocomplete
        @blur="emitBlur"
        v-model="currentUser"
        v-model:search="userSearch"
        :items="displayUsers"
        :loading="isLoading"
        variant="underlined"
        hide-no-data
        no-filter
        item-title="displayName"
        item-value="id"
        :label="label"
        :append-icon="!hideDetails && currentUser != null ? 'mdi-information-outline' : undefined"
        @click:append="showHideVCard"
        @click:clear="clear"
        :placeholder="$t('Prompt_TypeToSearch')"
        persistent-placeholder
        return-object
        clearable
        :user="user"
        :rules="rules"
        :error-messages="errorMessages"
        dense
        density="comfortable"
      >
        <template v-slot:item="{ props, item }">
          <v-list-item v-bind="props" :title="item.raw.displayName">
            <template v-if="!item.raw.generatedItem">
              <v-list-item-subtitle>{{ item.raw.jobTitle }}</v-list-item-subtitle>
            </template>
          </v-list-item>
        </template>
      </v-autocomplete>

      <span class="error--text" v-if="!user && required && checked">{{ requiredText || $t('Rule_Required') }}</span>
    </template>

    <v-expand-transition v-if="showVCard">
      <user-details @closeDetails="showHideVCard" :user="user"></user-details>
    </v-expand-transition>
  </div>
</template>

<script>
import axios from 'axios'
import UserDetails from '@/components/peoplePicker/UserDetails'
import AdminService from '@/services/admin.service'
import { debounce } from '@/common'
export default {
  emits: ['blur', 'clear', 'update:model-value', 'userChanged'],
  data() {
    return {
      displayUsers: [],
      isLoading: false,
      loginFailed: null,
      showIcon: false,
      showVCard: false,
      userImageSrc: null,
      userPhoto: '',
      users: [],
      userSearch: null,
      currentUser: this.user || null,
      cancelSource: []
    }
  },
  props: {
    user: {
      type: Object,
      default: null
    },
    label: {
      type: String,
      default: null
    },
    readonly: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    requiredText: {
      type: String,
      default: null
    },
    checked: {
      type: Boolean,
      default: false
    },
    rules: {
      type: Array,
      default: () => []
    },
    includeInactiveUsers: {
      type: Boolean,
      default: false
    },
    errorMessages: {
      type: String
    },
    includeDistroService: {
      type: Boolean,
      default: false
    },
    hideDetails: {
      type: Boolean,
      default: false
    },
    confidentialOptions: {
      type: Object,
      default: undefined
    }
  },
  methods: {
    emitBlur() {
      this.$emit('blur')
    },
    getUsers(query) {
      this.cancelGetUsers()
      let source = axios.CancelToken.source()
      this.cancelSource.push(source)

      AdminService.getUsersForPeoplePicker(
        source.token,
        this.confidentialOptionsComputed.confidentialCommunityAbbreviation,
        query,
        this.includeInactiveUsers,
        this.includeDistroService
      )
        .then((result) => {
          this.cancelSource = []
          this.users = result
        })
        .catch(() => {})
    },
    cancelGetUsers() {
      if (this.cancelSource) {
        this.cancelSource.forEach((ct) => {
          ct.cancel('Start new search, stop active search')
        })
      }
    },
    removeSelectedUser() {
      // this was put in place for when initiator and co-initiator are the same
      this.clear()
    },
    showHideVCard() {
      this.showVCard = !this.showVCard
    },
    clear() {
      this.users = []
      this.displayUsers = []
      this.currentUser = null
      this.$emit('clear')
    }
  },
  watch: {
    user: function (newValue) {
      if (newValue && newValue.displayName) {
        this.users = [newValue]
        this.currentUser = newValue
      } else {
        this.users = []
        this.currentUser = null
      }
      this.$emit('update:model-value', newValue)
    },
    users: function () {
      if (this.users.length === 0) {
        this.displayUsers = []
      } else {
        this.displayUsers = this.$_.sortBy(this.users, [
          (user) => {
            if (user.displayName) {
              user.displayName.toLowerCase()
            }
          }
        ])
      }
    },
    userSearch: debounce(function (q) {
      this.showVCard = false

      //if current user is same as the query then don't do the search
      if (this.user && this.user.displayName === q) {
        return
      }

      if (!q) {
        this.cancelGetUsers()
        this.clear()
        return
      }

      if (this.confidentialOptionsComputed.authorizedPeople) {
        this.users = this.confidentialOptionsComputed.authorizedPeople
          .filter((user) => q && q.trim() && user.displayName.toLowerCase().startsWith(q.toLowerCase()))
          .map((user) => ({ ...user }))
        return
      }

      if (q != null) {
        this.getUsers(q)
      }
    }, 10),

    currentUser: debounce(function (u) {
      if (u) {
        this.showIcon = true
        this.userSearch = u.displayName
        this.$emit('update:model-value', u)
        this.$emit('userChanged', u)
      } else {
        this.displayUsers = []
        this.users = []
      }
    }, 100)
  },
  computed: {
    confidentialOptionsComputed() {
      const injectedConfidentialOptionsProperties = this.getPeoplePickerInjectedProperties().confidentialOptions
      return this.confidentialOptions || injectedConfidentialOptionsProperties || {}
    }
  },
  components: {
    UserDetails
  },
  mounted() {
    let user = this.user
    if (user) {
      if (this.confidentialOptionsComputed.authorizedPeople) {
        user = this.confidentialOptionsComputed.authorizedPeople.find((person) => person.id === this.user.id)
      } else {
        this.users = [user]
      }
      this.$emit('update:model-value', user)
    }
  },
  inject: {
    getPeoplePickerInjectedProperties: {
      default: () => {
        return () => ({ confidentialOptions: undefined })
      }
    }
  }
}
</script>

<style scoped></style>
